import { createContext, useContext, type ReactNode } from "react";
import type { IAuthService } from "./interfaces/IAuthService";
import type { IQuestService } from "./interfaces/IQuestService";
import type { IQuestTemplateService } from "./interfaces/IQuestTemplateService";
import type { IBountyService } from "./interfaces/IBountyService";
import type { IUserService } from "./interfaces/IUserService";
import type { IPaymentService } from "./interfaces/IPaymentService";
import type { IStorageService } from "./interfaces/IStorageService";
import type { IAIService } from "./interfaces/IAIService";
import type { IWorkspaceService } from "./interfaces/IWorkspaceService";
import type { INegotiationService } from "./interfaces/INegotiationService";
import type { IAdminService } from "./interfaces/IAdminService";
import type { IStaffingAdminService } from "./interfaces/IStaffingAdminService";
import type { ICandidatesService } from "./interfaces/ICandidatesService";
import type { IComplianceService } from "./interfaces/IComplianceService";
import type { IQuestImageService } from "./interfaces/IQuestImageService";
import type { IQuestInstructionsService } from "./interfaces/IQuestInstructionsService";
import type { IBoardService } from "./interfaces/IBoardService";
import type { IOrganizationService } from "./interfaces/IOrganizationService";
import type { IOrgHubService } from "./interfaces/IOrgHubService";
import type { IAttentionService } from "./interfaces/IAttentionService";
import type { IChannelService } from "./interfaces/IChannelService";
import type { IAgentService } from "./interfaces/IAgentService";
import type { IBadgeService } from "./interfaces/IBadgeService";
import type { ICertificateService } from "./interfaces/ICertificateService";
import type { IExchangeService } from "./interfaces/IExchangeService";
import type { IActivityBadgeService } from "./interfaces/IActivityBadgeService";

export interface ServiceRegistry {
  auth: IAuthService;
  quests: IQuestService;
  /** DEV-318: stock quests are their own port, not more methods on quests. */
  questTemplates: IQuestTemplateService;
  bounties: IBountyService;
  users: IUserService;
  payments: IPaymentService;
  storage: IStorageService;
  ai: IAIService;
  workspace: IWorkspaceService;
  negotiations: INegotiationService;
  admin: IAdminService;
  staffing: IStaffingAdminService;
  candidates: ICandidatesService;
  /** DEV-783 step 7: the identity/terms/address-release block. */
  compliance: IComplianceService;
  /** Its own port, not more methods on quests: images answer to the bucket and
   *  two tables with their own RLS, which no quest mock should have to carry. */
  questImages: IQuestImageService;
  /** A separate table with its own RLS story (creator, admins, current
   *  assignees) — see IQuestInstructionsService. */
  questInstructions: IQuestInstructionsService;
  /** Private per-user kanban — owner-only RLS, no admin path. See IBoardService. */
  board: IBoardService;
  /** Organizations (20261007100000): every membership change is an RPC. */
  organizations: IOrganizationService;
  /** Org hub (2026-10-08): org work rollup, team chat, polls, files. Members only. */
  orgHub: IOrgHubService;
  /** "What needs you" — see IAttentionService. */
  attention: IAttentionService;
  /** The caller's own notification channels and their link flows. Admin reads stay on admin. */
  channels: IChannelService;
  /** AI agents the member owns (agent-access step 2). Edge-function only. */
  agents: IAgentService;
  /** Checklist badges (20261019140000). */
  badges: IBadgeService;
  /** Certificates of Completion (20261020120000). */
  certificates: ICertificateService;
  /** Skill / favor exchange pool (20261025100000). */
  exchange: IExchangeService;
  /** Activity badges (20261026120000): Reddit-style personalized badges. */
  activityBadges: IActivityBadgeService;
}

const ServiceContext = createContext<ServiceRegistry | null>(null);

interface ServiceProviderProps {
  registry: ServiceRegistry;
  children: ReactNode;
}

export function ServiceProvider({ registry, children }: ServiceProviderProps) {
  return (
    <ServiceContext.Provider value={registry}>
      {children}
    </ServiceContext.Provider>
  );
}

export function useServices(): ServiceRegistry {
  const ctx = useContext(ServiceContext);
  if (!ctx) {
    throw new Error("useServices must be used within a ServiceProvider");
  }
  return ctx;
}
